import type { CardDef, CardId, FighterId, SpaceId } from '@unmatched/engine';
import { content, portraitCard } from '@unmatched/content';
import { HAND_LIMIT, effectiveValue } from '@unmatched/engine';
import type React from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { PublicState } from '../api.js';
import { BoardCanvas } from '../board/BoardCanvas.js';
import type { BoardView } from '../board/renderer.js';
import { useServerGame } from '../game/useServerGame.js';
import { useStage, type FighterSnapshot } from '../game/useStage.js';
import { ActionBar, type ActionMode } from '../ui/ActionBar.js';
import { Card } from '../ui/Card.js';
import { CardPreview, type PreviewTarget } from '../ui/CardPreview.js';
import { CombatStage } from '../ui/CombatStage.js';
import { TurnBanner } from '../ui/TurnBanner.js';
import { AwayNotice } from '../ui/AwayNotice.js';
import { DiscardPile, DrawPile } from '../ui/Pile.js';
import { PileDialog } from '../ui/PileDialog.js';
import { GameHeader, SidePlayers } from '../ui/TopBar.js';
import { CardConfirm, type ConfirmKind } from '../ui/CardConfirm.js';
import { CombatPanel } from '../ui/CombatPanel.js';
import { GameOver } from '../ui/GameOver.js';
import { LogPanel } from '../ui/LogPanel.js';
import { MatchLobby } from './MatchLobby.js';
import { cardImageUrl } from '../cardImage.js';

interface Props {
  matchId: string;
  onLeave: () => void;
}

/** Portrét figúrky z artu jej vlastnej karty (zdroj obrázky postáv nemá). */
function portraitUrl(defId: string): string | undefined {
  const card = portraitCard(defId);
  return card ? cardImageUrl(card) : undefined;
}

/** Ikona figúrky sa berie z definície hrdinu alebo sidekicka. */
function iconFor(fighter: PublicState['fighters'][number]): string | undefined {
  const hero = content.heroes[fighter.defId];
  if (hero) return hero.icon;
  for (const candidate of Object.values(content.heroes)) {
    const sidekick = candidate.sidekicks?.find((s) => s.id === fighter.defId);
    if (sidekick) return sidekick.icon;
  }
  return undefined;
}

export function Game({ matchId, onLeave }: Props) {
  const game = useServerGame(matchId);
  const [selectedFighter, setSelectedFighter] = useState<FighterId | null>(null);
  const [selectedCard, setSelectedCard] = useState<CardId | null>(null);
  /** Čo si hráč vybral v akčnej lište — filtruje, ktoré karty sú v ruke aktívne. */
  const [mode, setMode] = useState<ActionMode | null>(null);
  /** Hráč zavrel nápis konca zápasu, aby si pozrel dosku. */
  const [endSeen, setEndSeen] = useState(false);
  /** Priebeh zápasu — predvolene skrytý, doska dostane viac miesta. */
  const [logOpen, setLogOpen] = useState(false);
  const [confirmAsk, setConfirmAsk] = useState<{ cardId: CardId; kind: ConfirmKind } | null>(null);
  /** Karta pod kurzorom — na arte je text primalý, tak sa ukáže vedľa väčšia. */
  /** Karta pod kurzorom aj s miestom, kde v ruke leží — náhľad sa podľa toho ukotví. */
  const [preview, setPreview] = useState<PreviewTarget | null>(null);

  /** Vlastné poradie kariet v ruke — hráč si ich smie prehadzovať. */
  const [order, setOrder] = useState<CardId[]>([]);
  const [dragCard, setDragCard] = useState<CardId | null>(null);
  /** Karty, ktoré práve letia do odhodu. */
  const [flights, setFlights] = useState<Flight[]>([]);
  const [pileOpen, setPileOpen] = useState(false);
  /** Hmla vybraná na presun (efekt „posuň hmlu"). */
  const [selectedFog, setSelectedFog] = useState<string | null>(null);

  const handRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ cardId: CardId; startX: number; moved: boolean } | null>(null);
  const dropRef = useRef(false);
  /** Posledná známa poloha karty v ruke — odtiaľ štartuje let do odhodu. */
  const slotRects = useRef(new Map<CardId, DOMRect>());
  const seenSeq = useRef(0);
  const primed = useRef(false);
  const flightKey = useRef(0);

  function handleCardHover(def: CardDef | null, rect?: DOMRect) {
    // Počas ťahania karty by náhľad zavadzal.
    if (dragRef.current?.moved) return;
    // Karta vystupuje z ruky nahor vždy; popis si k nej vedľa zavesí náhľad sám.
    setPreview(def && rect ? { def, rect, place: 'above' } : null);
  }

  /** Karta spomenutá v logu — náhľad ide vedľa, nie nad, nech riadok nezakryje. */
  function handleLogHover(def: CardDef | null, rect?: DOMRect) {
    setPreview(def && rect ? { def, rect, place: 'left' } : null);
  }

  const view = game.view;
  const state = view?.state;

  // Legálne akcie zo servera preusporiadané tak, ako sa na ne pýta UI.
  const options = useMemo(() => {
    const moves = new Map<FighterId, Map<SpaceId, SpaceId[]>>();
    const attacks: { fighterId: FighterId; targetId: FighterId; cardId: CardId }[] = [];
    const schemes = new Map<CardId, FighterId[]>();
    const defenses = new Set<CardId>();
    const discards = new Set<CardId>();
    const boosts = new Set<CardId>();
    /** Odpovede na rozhodnutie z efektu karty. */
    const effectMoves = new Map<FighterId, Map<SpaceId, SpaceId[]>>();
    const effectSpaces = new Set<SpaceId>();
    const effectTargets = new Set<FighterId>();
    /** Hmla → kam smie ísť. */
    const tokenMoves = new Map<string, Set<SpaceId>>();
    const tokenPlaces = new Set<SpaceId>();
    const cardChoices = new Set<CardId>();
    const optionChoices: number[] = [];
    const swapTargets = new Set<FighterId>();
    let canConfirm = false;
    let canSkip = false;
    let canManeuver = false;
    let canEndManeuver = false;
    let canEndTurn = false;
    let canPassDefense = false;

    for (const action of view?.legal ?? []) {
      switch (action.type) {
        case 'maneuver':
          canManeuver = true;
          break;
        case 'maneuverMove': {
          const target = action.path[action.path.length - 1]!;
          const perFighter = moves.get(action.fighterId) ?? new Map<SpaceId, SpaceId[]>();
          perFighter.set(target, action.path);
          moves.set(action.fighterId, perFighter);
          break;
        }
        case 'maneuverBoost':
          boosts.add(action.cardId);
          break;
        case 'maneuverEnd':
          canEndManeuver = true;
          break;
        case 'attack':
          attacks.push({
            fighterId: action.fighterId,
            targetId: action.targetId,
            cardId: action.cardId,
          });
          break;
        case 'scheme':
          schemes.set(action.cardId, [...(schemes.get(action.cardId) ?? []), action.fighterId]);
          break;
        case 'defend':
          if (action.cardId === null) canPassDefense = true;
          else defenses.add(action.cardId);
          break;
        case 'discard':
          discards.add(action.cardId);
          break;
        case 'endTurn':
          canEndTurn = true;
          break;
        case 'resolve':
          switch (action.choice.kind) {
            case 'move': {
              const target = action.choice.path[action.choice.path.length - 1]!;
              const perFighter =
                effectMoves.get(action.choice.fighterId) ?? new Map<SpaceId, SpaceId[]>();
              perFighter.set(target, action.choice.path);
              effectMoves.set(action.choice.fighterId, perFighter);
              break;
            }
            case 'place':
              effectSpaces.add(action.choice.spaceId);
              break;
            case 'damage':
              effectTargets.add(action.choice.targetId);
              break;
            case 'confirm':
              canConfirm = true;
              break;
            case 'skip':
              canSkip = true;
              break;
            case 'token': {
              const set = tokenMoves.get(action.choice.tokenId) ?? new Set<SpaceId>();
              set.add(action.choice.spaceId);
              tokenMoves.set(action.choice.tokenId, set);
              break;
            }
            case 'placeToken':
              tokenPlaces.add(action.choice.spaceId);
              break;
            case 'card':
              cardChoices.add(action.choice.cardId);
              break;
            case 'option':
              optionChoices.push(action.choice.index);
              break;
            case 'swap':
              swapTargets.add(action.choice.targetId);
              break;
          }
          break;
        default:
          break;
      }
    }
    return {
      moves,
      attacks,
      schemes,
      defenses,
      discards,
      boosts,
      canManeuver,
      canEndManeuver,
      canEndTurn,
      canPassDefense,
      effectMoves,
      effectSpaces,
      effectTargets,
      tokenMoves,
      tokenPlaces,
      cardChoices,
      optionChoices,
      swapTargets,
      canConfirm,
      canSkip,
    };
  }, [view?.legal]);

  const activePlayer = view?.viewer ?? 0;
  const inCombat = state?.combat != null;
  const inManeuver = state?.maneuver != null;
  /** Rozhodnutie, ktoré od hráča žiada efekt karty — má prednosť pred všetkým. */
  const pending = state?.pending?.[0] ?? null;
  const myPending = pending && pending.player === activePlayer ? pending : null;
  const activeHand = (state?.players[activePlayer]?.hand ?? []).filter(
    (id): id is string => id !== null,
  );
  const handKey = activeHand.join('|');

  /**
   * O koľko je ktorá karta v ruke silnejšia, než má vytlačené. Počíta to engine
   * z verejných údajov (Sinbadov odhod, kde ktorá figúrka začala ťah) — je to
   * len náhľad, o skutočnej hodnote rozhoduje server pri vyhodnotení súboja.
   */
  const valueBonuses = useMemo(() => {
    const out = new Map<CardId, number>();
    if (!state) return out;
    for (const cardId of activeHand) {
      const { base, value } = effectiveValue(
        state as unknown as Parameters<typeof effectiveValue>[0],
        content,
        activePlayer,
        cardId,
      );
      if (value !== base) out.set(cardId, value - base);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handKey, state, activePlayer]);

  // Poradie ruky je vec pohodlia jedného človeka, nie stav zápasu — server
  // o ňom nevie a drží sa v prehliadači. Kľúč je zápas + slot, aby si
  // v hot-seate hráči poradie neprepisovali.
  const orderKey = `unmatched:hand:${matchId}:${activePlayer}`;
  useEffect(() => {
    setOrder(readOrder(orderKey));
  }, [orderKey]);

  /**
   * Poradie ruky: najprv to, čo si hráč naposledy poukladal, potom novo
   * dotiahnuté karty na koniec. Karty, ktoré už v ruke nie sú, vypadnú samy,
   * takže sa poradie nemusí nikde upratovať.
   */
  const orderedHand = useMemo(() => {
    const inHand = new Set(activeHand);
    const kept = order.filter((id) => inHand.has(id));
    const keptSet = new Set(kept);
    return [...kept, ...activeHand.filter((id) => !keptSet.has(id))];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handKey, order]);

  /** Definícia konkrétnej karty v zápase. */
  /**
   * Karta čakajúca na potvrdenie odhodenia. Platí, len kým ju stav stále
   * ponúka — keď sa medzitým zmení (súper, koniec manévru), dialóg sám zmizne.
   */
  const confirmDef = confirmAsk ? defOf(confirmAsk.cardId) : null;
  const confirmValid = (() => {
    if (!confirmAsk) return false;
    const { cardId, kind } = confirmAsk;
    switch (kind) {
      case 'boostManeuver':
        return inManeuver && options.boosts.has(cardId);
      case 'handLimit':
        return options.discards.has(cardId);
      case 'boostCombat':
        return myPending?.kind === 'card' && myPending.purpose === 'boost' && options.cardChoices.has(cardId);
      case 'effect':
        return (
          myPending?.kind === 'card' &&
          (myPending.purpose === 'discard' || Boolean(myPending.discards)) &&
          options.cardChoices.has(cardId)
        );
    }
  })();

  function confirmCard() {
    if (!confirmAsk || !confirmValid) return;
    const { cardId, kind } = confirmAsk;
    setConfirmAsk(null);
    if (kind === 'boostManeuver') game.dispatch({ type: 'maneuverBoost', player: activePlayer, cardId });
    else if (kind === 'handLimit') game.dispatch({ type: 'discard', player: activePlayer, cardId });
    else game.dispatch({ type: 'resolve', player: activePlayer, choice: { kind: 'card', cardId } });
  }

  function defOf(cardId: CardId): CardDef | null {
    const instance = state?.cards[cardId];
    return instance ? (content.cards[instance.defId] ?? null) : null;
  }

  // Polohy slotov sa merajú po každom prekreslení — keď karta odíde z ruky,
  // už sa jej DOM nedá opýtať, odkiaľ má odletieť.
  useLayoutEffect(() => {
    const host = handRef.current;
    if (!host) return;
    for (const slot of host.querySelectorAll<HTMLElement>('.hand__slot')) {
      const id = slot.dataset.card;
      if (id) slotRects.current.set(id, slot.getBoundingClientRect());
    }
  });

  // Let do odhodu spúšťa udalosť z enginu, nie klik — takto sedí aj vtedy,
  // keď sa karta odhodí až po vyhodnotení súboja.
  useEffect(() => {
    const fresh = game.events.filter((event) => event.seq > seenSeq.current);
    if (fresh.length === 0) return;
    seenSeq.current = fresh[fresh.length - 1]!.seq;

    // Pri pripojení do rozohraného zápasu sa história nepreletuje.
    if (!primed.current) {
      primed.current = true;
      return;
    }

    const target = document.querySelector('.pile--discard .pile__slot')?.getBoundingClientRect();
    if (!target) return;

    const next: Flight[] = [];
    for (const event of fresh) {
      if (event.type !== 'cardDiscarded' || event.player !== activePlayer) continue;
      const from = slotRects.current.get(event.cardId);
      const def = defOf(event.cardId);
      if (!from || !def) continue;
      next.push({ key: ++flightKey.current, def, from, to: target });
      slotRects.current.delete(event.cardId);
    }
    if (next.length > 0) setFlights((current) => [...current, ...next]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.events, activePlayer]);

  // Jediná hmla na výber sa vyberie sama — netreba na ňu klikať.
  const fogChoices = myPending?.kind === 'token' ? [...options.tokenMoves.keys()] : [];
  const activeFog =
    myPending?.kind === 'token'
      ? selectedFog && options.tokenMoves.has(selectedFog)
        ? selectedFog
        : fogChoices.length === 1
          ? fogChoices[0]!
          : null
      : null;

  const reachable = useMemo(() => {
    // Pri rozhodnutí „polož figúrku" sú cieľom priestory, nie cesty.
    if (myPending?.kind === 'place') return options.effectSpaces;
    if (myPending?.kind === 'placeToken') return options.tokenPlaces;
    if (myPending?.kind === 'token') {
      return activeFog ? (options.tokenMoves.get(activeFog) ?? new Set<SpaceId>()) : new Set<SpaceId>();
    }
    if (!selectedFighter) return new Set<SpaceId>();
    if (myPending?.kind === 'move') {
      return new Set(options.effectMoves.get(selectedFighter)?.keys() ?? []);
    }
    return new Set(options.moves.get(selectedFighter)?.keys() ?? []);
  }, [
    options.moves,
    options.effectMoves,
    options.effectSpaces,
    options.tokenPlaces,
    options.tokenMoves,
    activeFog,
    selectedFighter,
    myPending,
  ]);

  const targets = useMemo(() => {
    if (myPending?.kind === 'damage') return options.effectTargets;
    if (myPending?.kind === 'swap') return options.swapTargets;
    if (!selectedCard) return new Set<FighterId>();
    return new Set(
      options.attacks
        .filter(
          (a) => a.cardId === selectedCard && (!selectedFighter || a.fighterId === selectedFighter),
        )
        .map((a) => a.targetId),
    );
  }, [options.attacks, options.effectTargets, options.swapTargets, selectedCard, selectedFighter, myPending]);

  /**
   * Figúrky, ktoré smú zahrať kartu pod kurzorom. Banner karty nesie ID figúrky
   * (`character`), `null` znamená ktorúkoľvek figúrku jej majiteľa — a majiteľ
   * sa zistí z balíčka, nie z toho, kto práve hrá: karta môže svietiť aj
   * z priebehu zápasu, kde patrí súperovi.
   */
  const bannerFighters = useMemo(() => {
    const def = preview?.def;
    if (!def || !state) return new Set<FighterId>();
    const owner = state.players.find((p) => content.heroes[p.heroId]?.deckId === def.deckId);
    if (!owner) return new Set<FighterId>();
    const theirs = state.fighters.filter((f) => f.owner === owner.index);
    const allowed = def.character ? theirs.filter((f) => f.defId === def.character) : theirs;
    return new Set(allowed.map((f) => f.id));
  }, [preview, state]);

  /** Figúrky pre réžiu súboja — pamätá si ich, aj keď zo stavu zmiznú. */
  const snapshots = useMemo<FighterSnapshot[]>(
    () =>
      (state?.fighters ?? []).map((f) => ({
        id: f.id,
        owner: f.owner,
        defId: f.defId,
        name: f.name,
        maxHp: f.maxHp,
        hp: f.hp,
      })),
    [state?.fighters],
  );
  const stage = useStage(game.events, snapshots);

  /**
   * Šípka útoku: počas scény súboja z réžie, inak zo stavu — ak sa hráč
   * pripojí uprostred súboja, udalosti o jeho začiatku už nepríde.
   */
  const combatArrow = stage.combat
    ? { attacker: stage.combat.attacker.id, defender: stage.combat.defender.id }
    : state?.combat
      ? { attacker: state.combat.attackerFighter, defender: state.combat.defenderFighter }
      : null;

  const boardView: BoardView = useMemo(
    () => ({
      tokens: (state?.fighters ?? []).map((f) => ({
        id: f.id,
        owner: f.owner,
        kind: f.kind,
        name: f.name,
        hp: f.hp,
        maxHp: f.maxHp,
        spaceId: f.spaceId,
        icon: iconFor(f),
        portrait: portraitUrl(f.defId),
      })),
      // „Polož figúrku…" (rozostavenie, Portal to Anywhere): zvýrazní tú, o ktorú ide.
      selectedFighter: myPending?.kind === 'place' ? myPending.fighterId : selectedFighter,
      reachable,
      targets,
      // Počas manévru sa dá hýbať len tými, ktoré sa ešte nepohli.
      controllable: new Set(
        myPending?.kind === 'move'
          ? [...options.effectMoves.keys()]
          : inManeuver
            ? [...options.moves.keys()]
            : (state?.fighters ?? [])
                .filter((f) => f.owner === activePlayer && !inCombat)
                .map((f) => f.id),
      ),
      banner: bannerFighters,
      activeOwner: state?.phase === 'ended' ? null : (state?.turn.player ?? null),
      combat: combatArrow,
      fog: (state?.tokens ?? []).map((t) => ({ id: t.id, spaceId: t.spaceId })),
      selectableFog: new Set(activeFog ? [] : fogChoices),
      selectedFog: activeFog,
    }),
    [
      state?.fighters,
      selectedFighter,
      myPending,
      reachable,
      targets,
      activePlayer,
      inCombat,
      inManeuver,
      options.moves,
      options.effectMoves,
      bannerFighters,
      myPending,
      state?.turn.player,
      state?.phase,
      combatArrow?.attacker,
      combatArrow?.defender,
      state?.tokens,
      activeFog,
      fogChoices.join('|'),
    ],
  );

  if (game.loading && !view) {
    return <div className="screen-message">{game.error ?? 'Načítavam zápas…'}</div>;
  }
  if (!view) return <div className="screen-message">{game.error ?? 'Zápas sa nenašiel'}</div>;

  // Online zápas pred štartom: pozvánka alebo výber postavy pozvaným hráčom.
  if (!state || view.status === 'lobby') {
    return (
      <MatchLobby
        view={view}
        connection={game.connection}
        busy={game.busy}
        onJoin={game.join}
        onLeave={onLeave}
      />
    );
  }

  const map = content.maps[state.mapId];
  if (!map) return <div className="screen-message">Neznáma mapa: {state.mapId}</div>;

  const yourTurn = view.activeSlot !== null && view.yourSlots.includes(view.activeSlot);
  const activeSeat = view.seats.find((s) => s.slot === view.activeSlot);
  const overHandLimit = options.discards.size > 0;
  const pendingBoost = state.maneuver?.pendingBoost ?? 0;
  const ended = state.phase === 'ended';

  // Náhľad ukazuje kartu z ruky aj z priebehu zápasu — bonus hľadáme podľa
  // definície, nie podľa konkrétneho kusa v ruke.
  const previewBonus = preview
    ? (activeHand
        .filter((cardId) => state.cards[cardId]?.defId === preview.def.id)
        .map((cardId) => valueBonuses.get(cardId) ?? 0)[0] ?? 0)
    : 0;

  // Pripojil sa uprostred súboja (refresh, prvé načítanie): udalosť o jeho
  // začiatku už nepríde, tak scénu poskladáme zo stavu.
  const sceneCombat =
    stage.combat ??
    (state.combat
      ? {
          key: -1,
          attacker: snapshotOf(state.combat.attackerFighter),
          defender: snapshotOf(state.combat.defenderFighter),
          // Súboj stojí uprostred vyhodnotenia (čaká na rozhodnutie) — karty
          // sú už odhalené a stav ich nesie.
          attackCard: state.combat.step === 'resolve' ? state.combat.attackCard : null,
          defenseCard: state.combat.step === 'resolve' ? state.combat.defenseCard : null,
          passed: state.combat.step === 'resolve' && !state.combat.defenseCard,
          attackValue: null,
          defenseValue: null,
          damage: null,
          hpAfter: null,
          defeated: false,
          cancelled: [],
          phase: state.combat.step === 'resolve' ? ('flip' as const) : ('declared' as const),
          ...(state.combat.bonus ? { bonus: state.combat.bonus.name } : {}),
        }
      : null);

  function snapshotOf(id: FighterId): FighterSnapshot {
    return (
      snapshots.find((f) => f.id === id) ?? { id, owner: 0, defId: '', name: id, maxHp: 0, hp: 0 }
    );
  }

  const me = state.players[activePlayer]!;
  const discardDefs = me.discard
    .map((cardId) => defOf(cardId))
    .filter((def): def is CardDef => def !== null)
    .reverse();
  const discardTop = discardDefs[0] ?? null;

  /**
   * Meno do hlavičky bloku v priebehu. V hot-seate sedia všetky sloty pod
   * jednou prezývkou, takže tam hráčov rozlíši jedine postava.
   */
  function nameOfPlayer(player: number): string {
    const seat = view!.seats.find((s) => s.slot === player);
    const hero = seat?.heroId ? content.heroes[seat.heroId]?.name : undefined;
    const nick = seat?.nick ?? undefined;
    const name = view!.mode === 'hotseat' ? (hero ?? nick) : (nick ?? (seat?.isBot && hero ? `${hero} (bot)` : hero));
    return name ?? `Hráč ${player + 1}`;
  }

  function clearSelection() {
    setSelectedFighter(null);
    setSelectedCard(null);
    setMode(null);
  }

  function handleFighterClick(fighterId: FighterId) {
    // Pri hmle, kladení žetónov a rozostavení (výmena sidekickov) je dôležitý priestor, nie figúrka na ňom.
    if (myPending?.kind === 'token' || myPending?.kind === 'placeToken' || myPending?.kind === 'place') {
      const fighter = state!.fighters.find((f) => f.id === fighterId);
      if (fighter) handleSpaceClick(fighter.spaceId);
      return;
    }
    if (myPending?.kind === 'swap') {
      if (options.swapTargets.has(fighterId)) {
        game.dispatch({ type: 'resolve', player: activePlayer, choice: { kind: 'swap', targetId: fighterId } });
      }
      return;
    }
    if (myPending) {
      // Poškodenie: klik na figúrku je rovno odpoveď. Presun: najprv výber.
      if (myPending.kind === 'damage' && options.effectTargets.has(fighterId)) {
        game.dispatch({
          type: 'resolve',
          player: activePlayer,
          choice: { kind: 'damage', targetId: fighterId },
        });
        setSelectedFighter(null);
        return;
      }
      if (myPending.kind === 'move' && options.effectMoves.has(fighterId)) {
        setSelectedFighter(fighterId === selectedFighter ? null : fighterId);
      }
      return;
    }
    if (selectedCard && targets.has(fighterId)) {
      const attack = options.attacks.find(
        (a) =>
          a.targetId === fighterId &&
          a.cardId === selectedCard &&
          (!selectedFighter || a.fighterId === selectedFighter),
      );
      if (attack) {
        game.dispatch({
          type: 'attack',
          player: activePlayer,
          fighterId: attack.fighterId,
          targetId: attack.targetId,
          cardId: attack.cardId,
        });
        clearSelection();
        return;
      }
    }
    if (inManeuver) {
      if (!options.moves.has(fighterId)) return;
      setSelectedFighter(fighterId === selectedFighter ? null : fighterId);
      return;
    }
    const fighter = state!.fighters.find((f) => f.id === fighterId);
    if (!fighter || fighter.owner !== activePlayer || inCombat) return;
    setSelectedFighter(fighterId === selectedFighter ? null : fighterId);
  }

  function handleSpaceClick(spaceId: SpaceId) {
    if (myPending?.kind === 'placeToken') {
      if (options.tokenPlaces.has(spaceId)) {
        game.dispatch({ type: 'resolve', player: activePlayer, choice: { kind: 'placeToken', spaceId } });
      }
      return;
    }
    if (myPending?.kind === 'token') {
      if (activeFog && options.tokenMoves.get(activeFog)?.has(spaceId)) {
        game.dispatch({
          type: 'resolve',
          player: activePlayer,
          choice: { kind: 'token', tokenId: activeFog, spaceId },
        });
        setSelectedFog(null);
        return;
      }
      // Klik na priestor s hmlou, ktorú efekt presúva, ju vyberie.
      const here = (state!.tokens ?? []).find((t) => t.spaceId === spaceId && options.tokenMoves.has(t.id));
      setSelectedFog(here ? here.id : null);
      return;
    }
    if (myPending) {
      if (myPending.kind === 'place' && options.effectSpaces.has(spaceId)) {
        game.dispatch({
          type: 'resolve',
          player: activePlayer,
          choice: { kind: 'place', spaceId },
        });
        return;
      }
      if (myPending.kind === 'move' && selectedFighter) {
        const path = options.effectMoves.get(selectedFighter)?.get(spaceId);
        if (!path) return;
        game.dispatch({
          type: 'resolve',
          player: activePlayer,
          choice: { kind: 'move', fighterId: selectedFighter, path },
        });
        setSelectedFighter(null);
      }
      return;
    }
    if (!inManeuver || !selectedFighter) return;
    const path = options.moves.get(selectedFighter)?.get(spaceId);
    if (!path) return;
    game.dispatch({ type: 'maneuverMove', player: activePlayer, fighterId: selectedFighter, path });
    setSelectedFighter(null);
  }

  function handleCardClick(cardId: CardId) {
    if (myPending?.kind === 'card') {
      if (!options.cardChoices.has(cardId)) return;
      // Odhodenie (za boost aj kvôli efektu) je nevratné — najprv potvrdenie.
      if (myPending.purpose === 'boost') setConfirmAsk({ cardId, kind: 'boostCombat' });
      else if (myPending.purpose === 'discard' || myPending.discards) setConfirmAsk({ cardId, kind: 'effect' });
      else game.dispatch({ type: 'resolve', player: activePlayer, choice: { kind: 'card', cardId } });
      return;
    }
    if (inCombat) {
      if (options.defenses.has(cardId))
        game.dispatch({ type: 'defend', player: activePlayer, cardId });
      return;
    }
    if (inManeuver) {
      if (options.boosts.has(cardId)) setConfirmAsk({ cardId, kind: 'boostManeuver' });
      return;
    }
    if (overHandLimit) {
      if (options.discards.has(cardId)) setConfirmAsk({ cardId, kind: 'handLimit' });
      return;
    }

    const schemeFighters = options.schemes.get(cardId);
    if (schemeFighters?.length && mode !== 'attack') {
      // Ak kartu môže riešiť viac figúrok, berieme vybranú, inak prvú možnú.
      const fighterId =
        (selectedFighter && schemeFighters.includes(selectedFighter) ? selectedFighter : null) ??
        schemeFighters[0]!;
      game.dispatch({ type: 'scheme', player: activePlayer, cardId, fighterId });
      clearSelection();
      return;
    }

    if (!options.attacks.some((a) => a.cardId === cardId)) return;
    setMode('attack');
    setSelectedCard(cardId === selectedCard ? null : cardId);
  }

  // --- prehadzovanie kariet v ruke -----------------------------------------
  // Poradie sa číta z DOM, nie z predchádzajúceho renderu — ťahanie generuje
  // pohyby rýchlejšie, než sa stihne prekresliť, a z DOM je vždy aktuálne.

  function handSlots(): HTMLElement[] {
    return [...(handRef.current?.querySelectorAll<HTMLElement>('.hand__slot') ?? [])];
  }

  function handleDragStart(event: React.PointerEvent<HTMLDivElement>, cardId: CardId) {
    if (event.button !== 0) return;
    dragRef.current = { cardId, startX: event.clientX, moved: false };
  }

  function handleDragMove(event: React.PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag) return;
    if (!drag.moved) {
      // Krátke potiahnutie je stále klik — inak by sa karta nedala zahrať.
      if (Math.abs(event.clientX - drag.startX) < 8) return;
      drag.moved = true;
      event.currentTarget.setPointerCapture(event.pointerId);
      setPreview(null);
      setDragCard(drag.cardId);
    }

    const slots = handSlots();
    const ids = slots.map((slot) => slot.dataset.card ?? '');
    const from = ids.indexOf(drag.cardId);
    if (from === -1) return;

    // Cieľ je slot, NAD ktorým kurzor je — nie ten, ku ktorého stredu je bližšie.
    // Ťahaná karta je stále v rade, takže polovičky by pri pohybe doprava
    // rátali o jedno miesto navyše. Medzera pred slotom patrí tomu slotu.
    let to = ids.length - 1;
    for (let i = 0; i < slots.length; i++) {
      if (event.clientX < slots[i]!.getBoundingClientRect().right) {
        to = i;
        break;
      }
    }
    if (to === from) return;

    const next = ids.slice();
    next.splice(to, 0, next.splice(from, 1)[0]!);
    setOrder(next);
    saveOrder(orderKey, next);
  }

  function handleDragEnd() {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag?.moved) return;
    // Po presúvaní nesmie prejsť klik, inak by sa karta rovno zahrala.
    dropRef.current = true;
    setTimeout(() => {
      dropRef.current = false;
    }, 0);
    setDragCard(null);
  }

  function swallowClickAfterDrag(event: React.MouseEvent) {
    if (!dropRef.current) return;
    dropRef.current = false;
    event.stopPropagation();
    event.preventDefault();
  }

  /** Kliknutie na akciu v lište: manéver sa spustí hneď, ostatné len prepnú režim. */
  function handleActionPick(picked: ActionMode) {
    if (picked === 'maneuver') {
      // Počas rozohraného manévru tlačidlo zostáva zvýraznené, ale druhý manéver
      // sa spustiť nedá — bez tejto poistky by klik vypýtal od servera 422.
      if (!options.canManeuver) return;
      game.dispatch({ type: 'maneuver', player: activePlayer });
      clearSelection();
      return;
    }
    setSelectedCard(null);
    setMode(mode === picked ? null : picked);
  }

  function cardPlayable(cardId: CardId): boolean {
    if (myPending?.kind === 'card') {
      return (myPending.owner ?? myPending.player) === activePlayer && options.cardChoices.has(cardId);
    }
    if (myPending) return false;
    if (inCombat) return options.defenses.has(cardId);
    if (inManeuver) return options.boosts.has(cardId);
    if (overHandLimit) return options.discards.has(cardId);
    const isScheme = options.schemes.has(cardId);
    const isAttack = options.attacks.some((a) => a.cardId === cardId);
    if (mode === 'scheme') return isScheme;
    if (mode === 'attack') return isAttack;
    return isScheme || isAttack;
  }

  function hint(): React.ReactNode {
    if (ended) return 'Zápas skončil.';
    if (pending) {
      if (!myPending) return <>Súper rieši efekt karty…</>;
      const what = {
        move: 'Vyber figúrku a klikni, kam ju presunúť.',
        place: 'Klikni na zvýraznený priestor.',
        damage: 'Klikni na figúrku, ktorú to zasiahne.',
        confirm: '',
        token: activeFog ? 'Klikni, kam hmlu presunúť.' : 'Klikni na priestor s hmlou, ktorú chceš presunúť.',
        placeToken: 'Klikni na zvýraznený priestor.',
        card:
          myPending.kind === 'card' && myPending.from === 'discard'
            ? 'Vyber kartu v odhode.'
            : myPending.kind === 'card' && myPending.from === 'deck'
              ? 'Vyber kartu z balíčka.'
            : myPending.kind === 'card' && myPending.purpose === 'effect' && myPending.discards
              ? 'Klikni na kartu v ruke — pôjde do odhodu. Alebo dosť.'
            : myPending.kind === 'card' && myPending.purpose === 'effect' && state?.reveal
              ? 'Klikni na kartu v ruke — súper ju neuvidí, kým nevyberie aj on.'
            : myPending.kind === 'card' && myPending.purpose === 'boost'
              ? 'Klikni na kartu v ruke — jej boost sa pripočíta. Alebo preskoč.'
              : myPending.kind === 'card' && (myPending.owner ?? myPending.player) !== activePlayer
                ? 'Vyber kartu zo súperovej ruky.'
                : 'Klikni na kartu v ruke.',
        option: 'Vyber jednu z možností dole.',
        swap: 'Klikni na zvýraznenú figúrku, alebo odmietni.',
        auto: '',
        bonusAttack: '',
      }[myPending.kind];
      return (
        <>
          <b>{myPending.label}</b>
          {what ? ` ${what}` : ''}
        </>
      );
    }
    if (inCombat) {
      return (
        <>
          Útočia na teba — vyber <b>obrannú kartu</b>, alebo sa nebráň.
        </>
      );
    }
    if (!yourTurn) {
      return (
        <>
          Na ťahu je <b>{activeSeat?.nick ?? 'súper'}</b>
          {game.connection === 'online' ? '' : ' · spojenie sa obnovuje'}
        </>
      );
    }
    if (overHandLimit) {
      return (
        <>
          Máš priveľa kariet — <b>odhoď na 7</b> kliknutím v ruke.
        </>
      );
    }
    if (inManeuver) {
      if (pendingBoost > 0 && !selectedFighter) {
        return (
          <>
            Boost <b>+{pendingBoost}</b> — vyber figúrku, ktorá pôjde ďalej.
          </>
        );
      }
      if (selectedFighter) return <>Klikni na zvýraznený priestor.</>;
      return (
        <>
          Presuň figúrky — <b>každou raz</b>. Kartou z ruky si predĺžiš pohyb o jej boost.
        </>
      );
    }
    if (mode === 'attack') {
      return selectedCard ? (
        <>
          Vyber <b>cieľ</b> na doske.
        </>
      ) : (
        <>
          Vyber <b>útočnú kartu</b> z ruky.
        </>
      );
    }
    if (mode === 'scheme') {
      return (
        <>
          Vyber <b>plán</b> z ruky.
        </>
      );
    }

    const left = 2 - state!.turn.actionsUsed.length;
    if (left === 0) return <>Obe akcie sú minuté — ukonči ťah.</>;
    return (
      <>
        Máš <b>{left === 2 ? 'dve akcie' : 'ešte jednu akciu'}</b>. Tú istú smieš použiť aj dvakrát.
      </>
    );
  }

  const zones = map.zones;

  return (
    <div className="app">
      <GameHeader
        state={state}
        view={view}
        connection={game.connection}
        nameOf={nameOfPlayer}
        onLeave={onLeave}
      />
      <main className="app__main">
        {/* Hráči po stranách dosky: ja vľavo, súper presne oproti. */}
        <aside className="game__side game__side--left">
          <SidePlayers state={state} view={view} nameOf={nameOfPlayer} side="left" />
          <CombatPanel
            combat={sceneCombat}
            pending={pending}
            viewer={activePlayer}
            nameOf={nameOfPlayer}
            defOf={defOf}
            announced={state?.combat?.announced}
          />
        </aside>

        <div className="boardWrap">
          <BoardCanvas
            map={map}
            view={boardView}
            effects={stage.effects}
            callbacks={{ onSpaceClick: handleSpaceClick, onFighterClick: handleFighterClick }}
          />

          {/* Legenda zón — farba priestoru je pravidlo (dosah ranged útoku). */}
          <div className="zoneLegend">
            <span className="eyebrow">Bojové zóny</span>
            {zones.map((zone) => (
              <span key={zone.id} className="zoneLegend__item">
                <span className="zoneLegend__dot" style={{ background: zone.color }} />
                {zone.name}
              </span>
            ))}
          </div>

          <button
            type="button"
            className={`logToggle ${logOpen ? 'is-open' : ''}`}
            onClick={() => setLogOpen((open) => !open)}
            aria-expanded={logOpen}
          >
            {logOpen ? 'Skryť priebeh ✕' : 'Priebeh ☰'}
          </button>

          <div className={`boardHint${yourTurn || myPending || inCombat ? ' is-mine' : ''}`}>
            {hint()}
          </div>

          {sceneCombat ? (
            <CombatStage
              show={sceneCombat}
              defOf={defOf}
              nameOf={nameOfPlayer}
              viewer={activePlayer}
              waiting={
                state.combat?.stage && pending
                  ? pending.player === activePlayer
                    ? 'Rozhodni sa — tlačidlá dole'
                    : 'Súper sa rozhoduje…'
                  : null
              }
              onDismiss={stage.dismiss}
            />
          ) : null}

          {ended && !sceneCombat && !endSeen ? (
            <GameOver
              state={state}
              myTeam={
                view.mode === 'hotseat' ? null : (state.players[view.yourSlots[0] ?? 0]?.team ?? null)
              }
              nameOf={nameOfPlayer}
              onClose={() => setEndSeen(true)}
              onLeave={onLeave}
            />
          ) : null}

          {stage.banner && !sceneCombat && !ended ? (
            <TurnBanner
              show={stage.banner}
              name={nameOfPlayer(stage.banner.player)}
              you={view.yourSlots.includes(stage.banner.player) && view.mode !== 'hotseat'}
            />
          ) : null}

          {!ended ? <AwayNotice view={view} /> : null}
        </div>

        <aside className="game__side game__side--right">
          <SidePlayers state={state} view={view} nameOf={nameOfPlayer} side="right" />
        </aside>

        {/* Priebeh je schovaný — vysunie sa na požiadanie ponad dosku. */}
        {logOpen ? (
          <div className="logDrawer">
            <LogPanel
              state={state}
              events={game.events}
              nameOf={nameOfPlayer}
              defOf={defOf}
              onCardHover={handleLogHover}
            />
          </div>
        ) : null}
      </main>

      {game.error ? <div className="error">{game.error}</div> : null}

      <ActionBar
        actionsUsed={state.turn.actionsUsed}
        actionsAllowed={2 + (state.turn.extraActions ?? 0)}
        choices={
          myPending?.kind === 'swap'
            ? [...options.swapTargets].map((targetId) => ({
                label: `Vymeniť s ${state.fighters.find((f) => f.id === targetId)?.name ?? targetId}`,
                onClick: () =>
                  game.dispatch({ type: 'resolve', player: activePlayer, choice: { kind: 'swap', targetId } }),
              }))
            : myPending?.kind === 'option'
            ? myPending.options
                .map((option, index) => ({ option, index }))
                .filter(({ index }) => options.optionChoices.includes(index))
                .map(({ option, index }) => ({
                  label: option.label,
                  onClick: () =>
                    game.dispatch({ type: 'resolve', player: activePlayer, choice: { kind: 'option', index } }),
                }))
            : []
        }
        available={{
          maneuver: options.canManeuver,
          scheme: options.schemes.size > 0,
          attack: options.attacks.length > 0,
        }}
        pending={Boolean(pending)}
        activeMode={inManeuver ? 'maneuver' : mode}
        busy={game.busy}
        canEndTurn={options.canEndTurn}
        finish={
          options.canConfirm
            ? {
                label: (myPending?.kind === 'confirm' && myPending.yes) || 'Áno',
                onClick: () =>
                  game.dispatch({
                    type: 'resolve',
                    player: activePlayer,
                    choice: { kind: 'confirm' },
                  }),
              }
            : options.canEndManeuver
              ? {
                  label: 'Hotovo',
                  onClick: () => {
                    game.dispatch({ type: 'maneuverEnd', player: activePlayer });
                    clearSelection();
                  },
                }
              : null
        }
        secondary={
          options.canSkip
            ? {
                // Voliteľný efekt („may", „až o N") sa smie nechať tak.
                label: options.canConfirm
                  ? (myPending?.kind === 'confirm' && myPending.no) || 'Nie'
                  : myPending?.kind === 'card' && myPending.purpose === 'discard'
                    ? 'Neodhodiť'
                    : myPending?.kind === 'card' && myPending.purpose === 'effect'
                      ? 'Dosť'
                    : myPending?.kind === 'place' && !myPending.source
                      ? 'Nechať, kde stojí'
                    : myPending?.kind === 'card' && myPending.purpose === 'boost'
                      ? 'Bez boostu'
                      : myPending?.kind === 'card' && myPending.purpose === 'replace'
                        ? 'Ponechať kartu'
                        : myPending?.kind === 'swap'
                          ? 'Nevymeniť'
                          : 'Preskočiť',
                onClick: () => {
                  game.dispatch({
                    type: 'resolve',
                    player: activePlayer,
                    choice: { kind: 'skip' },
                  });
                  setSelectedFighter(null);
                },
              }
            : inCombat && options.canPassDefense
              ? {
                  label: 'Nebrániť sa',
                  onClick: () =>
                    game.dispatch({ type: 'defend', player: activePlayer, cardId: null }),
                }
              : null
        }
        onPick={handleActionPick}
        onEndTurn={() => {
          game.dispatch({ type: 'endTurn', player: activePlayer });
          clearSelection();
        }}
      >
        <div className="hand">
          <DrawPile count={me.deckSize} />

          <div className="hand__cards" ref={handRef}>
            {orderedHand.map((cardId, index) => {
              const def = defOf(cardId);
              if (!def) return null;
              return (
                <div
                  key={cardId}
                  className={`hand__slot${dragCard === cardId ? ' is-dragging' : ''}`}
                  style={fanStyle(index, orderedHand.length)}
                  data-card={cardId}
                  onPointerDown={(event) => handleDragStart(event, cardId)}
                  onPointerMove={handleDragMove}
                  onPointerUp={handleDragEnd}
                  onPointerCancel={handleDragEnd}
                  onClickCapture={swallowClickAfterDrag}
                >
                  <Card
                    def={def}
                    disabled={!cardPlayable(cardId) || game.busy}
                    selected={selectedCard === cardId}
                    highlightBoost={inManeuver}
                    valueBonus={valueBonuses.get(cardId) ?? 0}
                    onClick={() => handleCardClick(cardId)}
                    onHover={handleCardHover}
                  />
                </div>
              );
            })}
            {orderedHand.length === 0 ? <div className="hand__empty">Prázdna ruka.</div> : null}
          </div>

          {(me.suit ?? []).length > 0 ? (
            // Vibranium Suit (Black Panther): súperove karty — len na boost.
            <div className="suit" aria-label="Vibranium Suit">
              <span className="suit__label">Vibranium Suit · len boost</span>
              <div className="suit__cards">
                {(me.suit ?? []).map((cardId) => {
                  const def = defOf(cardId);
                  if (!def) return null;
                  const usable = options.boosts.has(cardId) || (myPending?.kind === 'card' && options.cardChoices.has(cardId));
                  return (
                    <Card
                      key={cardId}
                      def={def}
                      disabled={!usable || game.busy}
                      highlightBoost
                      onClick={() => handleCardClick(cardId)}
                      onHover={handleCardHover}
                    />
                  );
                })}
              </div>
            </div>
          ) : null}

          <DiscardPile
            count={me.discard.length}
            top={discardTop}
            onOpen={() => setPileOpen(true)}
            onHover={handleCardHover}
          />
        </div>
      </ActionBar>

      <CardPreview target={preview} highlightBoost={inManeuver} valueBonus={previewBonus} />

      {flights.map((flight) => (
        <div
          key={flight.key}
          className="cardFly"
          style={flyStyle(flight)}
          onAnimationEnd={() =>
            setFlights((current) => current.filter((f) => f.key !== flight.key))
          }
        >
          <Card def={flight.def} />
        </div>
      ))}

      {myPending?.kind === 'card' && myPending.from === 'deck' ? (
        // Searching Strike: hľadanie vo vlastnom balíčku; Sight Beyond Sight: vrch súperovho.
        // Zoznam posiela server len tomu, kto vyberá.
        <PileDialog
          title={myPending.label}
          sub={
            (myPending.owner ?? myPending.player) === activePlayer
              ? 'Tvoj balíček — súper nevidí, čo v ňom je'
              : 'Vrch súperovho balíčka — vidíš ho len ty'
          }
          cards={[...options.cardChoices].map((id) => defOf(id)).filter((def): def is CardDef => def !== null)}
          pick={{
            cardIds: [...options.cardChoices].filter((id) => defOf(id) !== null),
            pickable: options.cardChoices,
            onPick: (cardId) =>
              game.dispatch({ type: 'resolve', player: activePlayer, choice: { kind: 'card', cardId } }),
          }}
          onClose={() =>
            myPending.optional
              ? game.dispatch({ type: 'resolve', player: activePlayer, choice: { kind: 'skip' } })
              : undefined
          }
        />
      ) : null}

      {myPending?.kind === 'card' && myPending.from === 'hand' && (myPending.owner ?? myPending.player) !== activePlayer ? (
        // Set the Stage: Houdini vyberá kartu, ktorú súper odhodí — zo súperovej ruky.
        <PileDialog
          title={myPending.label}
          sub="Súperova ruka — vidíš ju vďaka efektu karty"
          cards={[...options.cardChoices].map((id) => defOf(id)).filter((def): def is CardDef => def !== null)}
          pick={{
            cardIds: [...options.cardChoices].filter((id) => defOf(id) !== null),
            pickable: options.cardChoices,
            onPick: (cardId) =>
              game.dispatch({ type: 'resolve', player: activePlayer, choice: { kind: 'card', cardId } }),
          }}
          onClose={() => undefined}
        />
      ) : null}

      {confirmAsk && confirmValid && confirmDef ? (
        <CardConfirm
          def={confirmDef}
          kind={confirmAsk.kind}
          remaining={activeHand.length - HAND_LIMIT}
          onConfirm={confirmCard}
          onCancel={() => setConfirmAsk(null)}
        />
      ) : null}

      {pileOpen || (myPending?.kind === 'card' && myPending.from === 'discard') ? (
        <PileDialog
          title={myPending?.kind === 'card' ? myPending.label : 'Odhadzovací balíček'}
          sub={`${me.discard.length} kariet · od naposledy odhodenej`}
          cards={discardDefs}
          pick={
            myPending?.kind === 'card' && myPending.from === 'discard'
              ? {
                  cardIds: me.discard.slice().reverse(),
                  pickable: options.cardChoices,
                  onPick: (cardId) =>
                    game.dispatch({ type: 'resolve', player: activePlayer, choice: { kind: 'card', cardId } }),
                }
              : undefined
          }
          onClose={() => setPileOpen(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * Vejár ruky: karty sú mierne natočené okolo stredu a krajné klesnú, ako keď
 * ich človek drží v ruke. Iba vizuál — poradie aj ťahanie idú podľa DOM.
 */
function fanStyle(index: number, total: number): React.CSSProperties {
  const middle = (total - 1) / 2;
  const offset = index - middle;
  const spread = Math.min(4, 22 / Math.max(total, 1));
  return {
    ['--fan-rot' as string]: `${offset * spread}deg`,
    ['--fan-y' as string]: `${Math.abs(offset) ** 1.6 * 3}px`,
  } as React.CSSProperties;
}

/** Karta na ceste z ruky do odhodu. */
interface Flight {
  key: number;
  def: CardDef;
  from: DOMRect;
  to: DOMRect;
}

/**
 * Let karty z ruky do odhodu. Posun sa odovzdá ako CSS premenné, aby samotnú
 * dráhu (aj oblúk) držal štýl a nie JavaScript.
 */
function flyStyle(flight: Flight): React.CSSProperties {
  return {
    left: flight.from.left,
    top: flight.from.top,
    ['--dx' as string]: `${flight.to.left - flight.from.left}px`,
    ['--dy' as string]: `${flight.to.top - flight.from.top}px`,
  } as React.CSSProperties;
}

/** Uložené poradie ruky. Súkromné okno alebo zakázané úložisko nie je chyba. */
function readOrder(key: string): CardId[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? 'null');
    return Array.isArray(parsed) ? parsed.filter((id): id is CardId => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

function saveOrder(key: string, order: CardId[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(order));
  } catch {
    /* bez úložiska sa poradie len neprenesie cez refresh */
  }
}
